<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';

const toast = useToast();

const showErrorDialog = ref(false);
const lastError = ref<Error | null>(null);
const lastErrorInfo = ref('');
const showDetails = ref(false);

function handleGlobalError(event: ErrorEvent) {
  event.preventDefault();

  const error = event.error || new Error(event.message);
  lastError.value = error;
  lastErrorInfo.value = `Unhandled error at ${event.filename}:${event.lineno}:${event.colno}`;

  // Log to console (could later integrate with Overwatch SDK)
  console.error('Global error handler caught:', error);

  toast.add({
    severity: 'error',
    summary: 'An error occurred',
    detail: error.message,
    life: 5000,
  });
}

function handleUnhandledRejection(event: PromiseRejectionEvent) {
  event.preventDefault();

  const error =
    event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
  lastError.value = error;
  lastErrorInfo.value = 'Unhandled promise rejection';

  // Log to console (could later integrate with Overwatch SDK)
  console.error('Unhandled promise rejection:', error);

  toast.add({
    severity: 'error',
    summary: 'An error occurred',
    detail: error.message,
    life: 5000,
  });
}

function showErrorDetails() {
  if (lastError.value) {
    showErrorDialog.value = true;
  }
}

function copyErrorToClipboard() {
  if (lastError.value) {
    const errorText = `Error: ${lastError.value.message}\n\nStack:\n${lastError.value.stack || 'N/A'}\n\nInfo: ${lastErrorInfo.value}`;
    navigator.clipboard.writeText(errorText).then(() => {
      toast.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Error details copied to clipboard',
        life: 3000,
      });
    });
  }
}

function clearError() {
  lastError.value = null;
  lastErrorInfo.value = '';
  showErrorDialog.value = false;
  showDetails.value = false;
}

onMounted(() => {
  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
});

onUnmounted(() => {
  window.removeEventListener('error', handleGlobalError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
});

// Expose for programmatic use
defineExpose({ showErrorDetails, clearError, lastError });
</script>

<template>
  <Dialog
    v-model:visible="showErrorDialog"
    header="Error Details"
    modal
    :style="{ width: '500px' }"
  >
    <div class="space-y-4">
      <div>
        <h4 class="mb-1 text-sm font-medium text-gray-500">Error Message</h4>
        <p class="rounded bg-red-50 p-2 text-red-700">
          {{ lastError?.message }}
        </p>
      </div>

      <div>
        <Button
          :label="showDetails ? 'Hide Stack Trace' : 'Show Stack Trace'"
          text
          size="small"
          @click="showDetails = !showDetails"
        />
        <pre
          v-if="showDetails && lastError?.stack"
          class="mt-2 max-h-48 overflow-auto rounded bg-gray-100 p-2 text-xs"
          >{{ lastError.stack }}</pre
        >
      </div>

      <div v-if="lastErrorInfo">
        <h4 class="mb-1 text-sm font-medium text-gray-500">Context</h4>
        <p class="text-sm text-gray-600">{{ lastErrorInfo }}</p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Copy Details"
          icon="pi pi-copy"
          severity="secondary"
          @click="copyErrorToClipboard"
        />
        <Button label="Close" @click="clearError" />
      </div>
    </template>
  </Dialog>
</template>
